import { AfterViewInit, Component, OnInit } from '@angular/core';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { SwisslifeLocationService } from '@app/services/swisslife-location.service';
import { Big } from 'big.js';
import * as _ from 'lodash';
import { PledgeRead } from '../../definition/interface/Pledge';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { SupportsTable } from '../operations-form/shared/supports-table/supports-table.model';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';
@UntilDestroy()
@Component({
  selector: 'isin-table',
  templateUrl: './isin-table.component.html',
  styleUrls: ['./isin-table.component.scss'],
  standalone: false,
})
export class IsinTableComponent
  extends EaSubscriptionBuilder
  implements OnInit, AfterViewInit
{
  hideAlert = false;
  _ = _;
  selectedRadioValue: string;
  clientRiskScore: number;
  pledge: PledgeRead;
  supportRepartitionType: 'AMOUNT' | 'PERCENTAGE' = 'PERCENTAGE';
  avMonthlyPayment: number;
  avInitialPayment: number;
  avInvestmentType: string;
  isSwisslifeProduct: boolean = false;
  initialTable: SupportsTable;
  monthlyTable: SupportsTable;
  // libellés français des types de support (référentiel), indexés par id (ex. 'STRUCTURED_PRODUCTS')
  private supportTypeLabels: Record<string, string> = {};

  constructor(
    protected formConstructor: PrFormConstructor,
    private subscriptionFacade: SubscriptionFacadeService,
    private swisslifeLocation: SwisslifeLocationService,
    private referenceDao: ReferenceDao,
  ) {
    super(formConstructor);
  }
  ngAfterViewInit(): void {}

  ngOnInit() {
    this.pledge = this.subscriptionFacade.getPledge();
    this.avInitialPayment =
      this.pledge?.products?.[0]?.parameters?.avInitialPayment;
    this.avMonthlyPayment =
      this.pledge?.products?.[0]?.parameters?.avMonthlyPayment;
    this.supportRepartitionType =
      this.pledge?.products?.[0]?.parameters?.supportRepartition?.[0]
        ?.supportRepartitionType ?? 'PERCENTAGE';
    this.avInvestmentType =
      this.pledge?.products?.[0]?.parameters?.avInvestmentType;

    this.clientRiskScore = this.pledge?.owners?.[0]?.riskProfil?.score;
    this.isSwisslifeProduct =
      this.swisslifeLocation.hasSwisslifeProduct(this.pledge);

    this.referenceDao
      .getSupportTypes()
      .pipe(untilDestroyed(this))
      .subscribe(types => {
        this.supportTypeLabels = Object.fromEntries(
          (types || []).map((type: any) => [type.id, type.value]),
        );
      });

    this.subscriptionFacade.initForm(this);
    this.buildTables();
  }

  get showInitialTable(): boolean {
    return (
      ['INITIAL', 'INITIAL_AND_MONTHLY'].includes(
        this.options?.parameters?.avInvestmentType,
      ) && !!this.options?.parameters?.avInitialPayment
    );
  }

  get showMonthlyTable(): boolean {
    return (
      ['MONTHLY', 'INITIAL_AND_MONTHLY'].includes(
        this.options?.parameters?.avInvestmentType,
      ) && !!this.options?.parameters?.avMonthlyPayment
    );
  }

  get tableColumnClass(): string {
    return this.showInitialTable && this.showMonthlyTable
      ? 'col-xs-12 col-md-6'
      : 'col-xs-12';
  }

  private buildTables() {
    const isAmount =
      (this.selectedRadioValue || this.supportRepartitionType) === 'AMOUNT';
    const unit = isAmount ? '€' : '%';
    const label = isAmount ? 'En €' : 'En %';
    const removeComment =
      'Toute suppression est définitive, les données seront perdues.';

    this.initialTable = {
      title: `Versement initial ${this.options?.parameters?.avInitialPayment} €`,
      removable: !this.options?.parameters?.avMonthlyPayment,
      minRows: 1,
      removeComment,
      emptyLabel: 'Aucun support sélectionné pour le moment.',
      getTypeLabel: this.getSupportTypeLabel.bind(this),
      columns: [
        { label: 'Support', type: 'support' },
        {
          label,
          type: 'input',
          controlName: 'initalRepartition',
          unit,
          numeric: true,
          ariaLabel: isAmount
            ? 'Répartition du versement initial en euros'
            : 'Répartition du versement initial en pourcentage',
        },
      ],
    };

    this.monthlyTable = {
      title: `Versement mensuel ${this.options?.parameters?.avMonthlyPayment} €`,
      removable: !!this.options?.parameters?.avMonthlyPayment,
      minRows: 1,
      removeComment,
      emptyLabel: 'Aucun support sélectionné pour le moment.',
      getTypeLabel: this.getSupportTypeLabel.bind(this),
      columns: [
        { label: 'Support', type: 'support' },
        {
          label,
          type: 'input',
          controlName: 'monthlyRepartition',
          unit,
          numeric: true,
          ariaLabel: isAmount
            ? 'Répartition du versement mensuel en euros'
            : 'Répartition du versement mensuel en pourcentage',
        },
      ],
    };
  }

  getSupportTypeLabel(typeId: string): string {
    return this.supportTypeLabels[typeId] || typeId;
  }

  floored(value) {
    return new Big(value).round(2).toNumber();
  }
  getTotalAvInitial(data) {
    return (data?.totalAvInitialPayment * data?.initalRepartition) / 100;
  }
  getTotalAvMonthly(data) {
    return (data?.totalAvMonthlyPayment * data?.monthlyRepartition) / 100;
  }

  get totalAllocatedAvInitial() {
    return this.calculateTotalAllocated(
      this.supportRepartition.value,
      'initalRepartition',
    );
  }
  get totalAllocatedAvMonthly() {
    return this.calculateTotalAllocated(
      this.supportRepartition.value,
      'monthlyRepartition',
    );
  }
  calculateTotalAllocated(data, percentKey) {
    return new Big(
      this.floored(
        _(data)
          ?.map(percentKey)
          ?.map(percent => (percent ? +percent : 0))
          ?.sum(),
      ) ?? 0,
    )
      .div(100)
      .toNumber();
  }
  get riskNoteInitial() {
    // if we don't have the client risk score, we can't calculate anything
    if (this.clientRiskScore == null) {
      return null;
    }
    //if allocation is not complete, we can't calculate anything
    if (this.totalAllocatedAvInitial != 1) {
      return null;
    }

    if (!['INITIAL', 'INITIAL_AND_MONTHLY'].includes(this.avInvestmentType)) {
      return null;
    }

    const totalAvInitial = this.options?.parameters?.avInitialPayment;

    const data = this.supportRepartition?.value;
    // Initialize the total risk contribution
    let riskContratInitial = 0;

    // Iterate over the array and calculate the contribution for each item
    for (let item of data) {
      // if at least one risk score is null, we can't calculate anything
      if (item.riskScore == null) {
        return null;
      }

      const { supportRepartitionType = 'PERCENTAGE' } = item;
      let value = item?.initalRepartition ? item?.initalRepartition : 0;

      const percentage =
        supportRepartitionType === 'PERCENTAGE'
          ? value / 100
          : value / totalAvInitial;

      riskContratInitial += item.riskScore * percentage;
    }
    if (riskContratInitial < this.clientRiskScore - 0.75) {
      return {
        display: 'warning',
        icon: 'warning',
        text:
          `Les versements initiaux de l’allocation que vous avez choisie présente un couple rendement/risque plus faible que le profil de risque que vous avez défini.
        (niveau de risque du contrat de ` +
          riskContratInitial?.toFixed(1) +
          ` pour un profil de risque de ` +
          this.clientRiskScore?.toFixed(1) +
          `)`,
      };
    }
    if (riskContratInitial > this.clientRiskScore) {
      return {
        display: 'warning',
        icon: 'warning',
        text:
          `Les versements initiaux de l’allocation que vous avez choisie présente une note de risque supérieure à votre profil de risque. Votre allocation peut engendrer un rendement important mais aussi une exposition plus élevée aux fluctuations du marché, tant à la hausse qu’à la baisse.
        (niveau de risque du contrat de ` +
          riskContratInitial?.toFixed(1) +
          ` pour un profil de risque de ` +
          this.clientRiskScore?.toFixed(1) +
          `)`,
      };
    }
    return null;
  }
  get riskNoteMonthly() {
    // if we don't have the client risk score, we can't calculate anything
    if (this.clientRiskScore == null) {
      return null;
    }
    //if allocation is not complete, we can't calculate anything
    if (this.totalAllocatedAvMonthly !== 1) {
      return null;
    }

    if (!['MONTHLY', 'INITIAL_AND_MONTHLY'].includes(this.avInvestmentType)) {
      return null;
    }

    const totalAvMonthly = this.options?.parameters?.avMonthlyPayment;

    const data = this.supportRepartition?.value;
    let riskContrat = 0;

    for (let item of data) {
      // if at least one risk score is null, we can't calculate anything
      if (item.riskScore == null) {
        return null;
      }

      const { supportRepartitionType = 'PERCENTAGE' } = item;
      let value = item?.monthlyRepartition ? item?.monthlyRepartition : 0;

      const percentage =
        supportRepartitionType === 'PERCENTAGE'
          ? value / 100
          : value / totalAvMonthly;

      riskContrat += item.riskScore * percentage;
    }

    if (riskContrat < this.clientRiskScore - 0.75) {
      return {
        display: 'warning',
        icon: 'warning',
        text:
          `Les versements programmés de l’allocation que vous souhaitez mettre en place présenteront un couple rendement/risque plus faible que le profil de risque que vous avez défini.
        (niveau de risque du contrat de ` +
          riskContrat?.toFixed(1) +
          ` pour un profil de risque de ` +
          this.clientRiskScore?.toFixed(1) +
          `)`,
      };
    }
    if (riskContrat > this.clientRiskScore) {
      return {
        display: 'warning',
        icon: 'warning',
        text:
          `Les versements programmés de l’allocation que vous souhaitez mettre en place présenteront ont une note de risque supérieure à votre profil de risque. Votre future allocation pourra engendrer un rendement important mais aussi une exposition plus élevée aux fluctuations du marché, tant à la hausse qu’à la baisse.
        (niveau de risque du contrat de ` +
          riskContrat?.toFixed(1) +
          ` pour un profil de risque de ` +
          this.clientRiskScore?.toFixed(1) +
          `)`,
      };
    }
    return null;
  }
  getAllocationPanel(percentKey, totalPaymentKey, isInitial) {
    if (!this.supportRepartition?.value?.length) {
      return {
        display: 'info',
        icon: 'info',
        text: 'Renseignez vos investissements',
      };
    }

    const totalAllocated = this.calculateTotalAllocated(
      this.supportRepartition.value,
      percentKey,
    );
    const amount =
      percentKey == 'monthlyRepartition'
        ? this.avMonthlyPayment
        : this.avInitialPayment;
    const totalAmount = _(this.supportRepartition.value)
      ?.map(percentKey)
      ?.map(percent => (percent ? +percent : 0))
      ?.sum();

    const conditions = [
      {
        condition: totalAllocated > 1,
        result: {
          display: 'danger',
          icon: 'error',
          text: `${new Big(totalAllocated).times(100).toNumber()}% au total - Vous ne pouvez pas repartir plus de 100% de l'investissement`,
        },
      },
      {
        condition: totalAllocated < 1,
        result: {
          display: 'warning',
          icon: 'warning',
          text: `Il reste ${new Big(amount).times(
            new Big(1).minus(totalAllocated),
          )}€ de votre investissement à répartir, soit ${new Big(1)
            .minus(totalAllocated)
            .times(100)}%`,
        },
      },
      {
        condition:
          _.filter(this.supportRepartition.value, scpi => !scpi[percentKey])
            .length && totalAllocated !== 1,
        result: {
          display: 'danger',
          icon: 'error',
          text: 'Vous ne pouvez pas repartir 0% dans un support',
        },
      },
      {
        condition: totalAllocated === 1,
        result: {
          display: 'success',
          icon: 'check',
          text: 'Félicitations, vos investissements sont répartis à 100%',
        },
      },
    ];
    const amountConditions = [
      {
        condition: totalAmount > amount,
        result: {
          display: 'danger',
          icon: 'error',
          text: `${totalAmount}€ au total - Vous ne pouvez pas repartir plus de ${amount}€ de l'investissement`,
        },
      },
      {
        condition: totalAmount < amount,
        result: {
          display: 'warning',
          icon: 'warning',
          text: `Il reste ${new Big(amount).minus(totalAmount).toNumber()}€ de votre investissement à répartir sur ${amount}€`,
        },
      },
      {
        condition:
          _.filter(this.supportRepartition.value, scpi => !scpi[percentKey])
            .length && totalAmount != amount,
        result: {
          display: 'danger',
          icon: 'error',
          text: `Vous ne pouvez pas repartir 0€ dans un support `,
        },
      },
      {
        condition: totalAmount === amount,
        result: {
          display: 'success',
          icon: 'check',
          text: `Félicitations, vos investissements sont répartis à ${amount}€`,
        },
      },
    ];
    const switchCondition =
      this.selectedRadioValue == 'AMOUNT' ? amountConditions : conditions;

    for (const condition of switchCondition) {
      if (condition.condition) {
        return condition.result;
      }
    }

    return null;
  }
  get allocationPanelAvInitial() {
    return this.getAllocationPanel(
      'initalRepartition',
      'totalAvInitialPayment',
      true,
    );
  }

  get allocationPanelAvMonthly() {
    return this.getAllocationPanel(
      'monthlyRepartition',
      'totalAvMonthlyPayment',
      false,
    );
  }

  getMessage(type: 'initial' | 'monthly') {
    return type === 'initial'
      ? {
          display: 'success',
          icon: 'check',
          text: this.totalAllocatedAvInitial * 100 + '%',
        }
      : {
          display: 'success',
          icon: 'check',
          text: this.totalAllocatedAvMonthly * 100 + '%',
        };
  }
  deleteFields(index) {
    this.supportRepartition.removeAt(index);

    this.supportRepartition.markAsDirty();
  }
  onSubmit() {
    this.subscriptionFacade.selectErrorInputAndScroll(this);
    this.form.markAsDirty();
    this.displayErrors();

    if (this.form.valid) {
      this.subscriptionFacade.setStepName('isinTable');
      this.subscriptionFacade.submitPledge(this);
    }
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }
  toggleColumnType(event) {
    this.selectedRadioValue = event.id;

    // Caution: There is a potential issue here that needs attention.
    // pr-radio call on change event the first whithout value set
    // we must to check all component that use this pr-radio component
    if (this.supportRepartitionType == this.selectedRadioValue) {
      this.buildTables();
      return;
    }
    this.supportRepartitionType = event.id;
    for (let i = 0; i < this.supportRepartition.length; i++) {
      this.supportRepartition
        .get(`${i}`)
        ?.get('supportRepartitionType')
        ?.setValue(this.selectedRadioValue);

      const monthlyRepartition = this.supportRepartition
        .get(`${i}`)
        .get('monthlyRepartition');
      const initalRepartition = this.supportRepartition
        .get(`${i}`)
        .get('initalRepartition');
      if (this.avInitialPayment)
        initalRepartition.setValue(
          this.selectedRadioValue === 'PERCENTAGE'
            ? new Big(initalRepartition.value)
                .div(this.avInitialPayment)
                .times(100)
                .round(3)
            : new Big(initalRepartition.value)
                .times(this.avInitialPayment)
                .div(100),
        );

      if (this.avMonthlyPayment)
        monthlyRepartition.setValue(
          this.selectedRadioValue === 'PERCENTAGE'
            ? new Big(monthlyRepartition.value)
                .div(this.avMonthlyPayment)
                .times(100)
                .round(3)
            : new Big(monthlyRepartition.value)
                .times(this.avMonthlyPayment)
                .div(100),
        );
    }

    this.form.updateValueAndValidity();
    this.buildTables();
  }
}
